import { useQuery } from '@tanstack/react-query';
import { Loader2, Lock, RefreshCw, Sparkles } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input, Label, Textarea } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { api } from '@/lib/api';
import type { ArchetypeOption, CharacterDraft as ClaudeDraft, Pillar } from '@/types/persona';

/** Operator-editable "Define the character" fields. Empty = Claude decides. */
export interface CharacterDraft {
  audience: string;
  displayName: string;
  bio: string;
  visualIdentity: string;
  tone: string;
  pillars: Pillar[];
  extraClaims: string;
}

export const BIO_SOFT_MAX = 120;

export function characterFromArchetype(a: ArchetypeOption | undefined): CharacterDraft {
  const pillars = a?.pillars?.length === 3 ? a.pillars : [];
  return {
    audience: a?.audience ?? '',
    displayName: '',
    bio: '',
    visualIdentity: a?.visual_archetype ?? '',
    tone: '',
    pillars: [0, 1, 2].map((i) => ({
      name: pillars[i]?.name ?? '',
      description: pillars[i]?.description ?? '',
    })),
    extraClaims: '',
  };
}

/** Claude's draft → form values (every field filled, all still editable). */
export function characterFromDraft(d: ClaudeDraft): CharacterDraft {
  return {
    audience: d.audience,
    displayName: d.display_name,
    bio: d.bio,
    visualIdentity: d.visual_identity,
    tone: d.tone.join(', '),
    pillars: [0, 1, 2].map((i) => ({
      name: d.pillars[i]?.name ?? '',
      description: d.pillars[i]?.description ?? '',
    })),
    extraClaims: d.claims_boundaries.join('\n'),
  };
}

/** Pillars are all-or-nothing: three names, or none (Claude generates). */
export function pillarsError(pillars: Pillar[]): string | null {
  const named = pillars.filter((p) => p.name.trim()).length;
  return named === 0 || named === 3 ? null : 'Name all three pillars, or clear them all.';
}

function Step({
  n,
  label,
  locked,
  hint,
  children,
}: {
  n: number;
  label: string;
  locked?: boolean;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-2">
        <Label>
          <span className="mr-1.5 text-muted-foreground">{n}.</span>
          {label}
        </Label>
        {locked ? (
          <Badge tone="neutral">
            <Lock className="h-3 w-3" /> predefined
          </Badge>
        ) : (
          <Badge tone="running">editable</Badge>
        )}
      </div>
      {hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
      {children}
    </div>
  );
}

/** Full-screen, centered loader while Claude drafts the character. */
function DraftingOverlay({ label }: { label: string | undefined }) {
  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/70 backdrop-blur-sm"
    >
      <div className="flex w-full max-w-sm flex-col items-center gap-4 rounded-xl border border-border bg-card px-8 py-7 text-center shadow-2xl">
        <div className="relative flex h-14 w-14 items-center justify-center">
          <Loader2 className="absolute h-14 w-14 animate-spin text-primary/30" strokeWidth={1.5} />
          <Sparkles className="h-6 w-6 text-primary" />
        </div>
        <div className="space-y-1">
          <p className="text-base font-semibold tracking-tight">
            Drafting {label ? `your ${label}` : 'the character'}…
          </p>
          <p className="text-sm text-muted-foreground">
            Claude is writing the audience, name, bio, look, voice and three content pillars. This
            takes about 15 seconds.
          </p>
        </div>
      </div>
    </div>
  );
}

export function CharacterCard({
  archetype,
  value,
  onChange,
  drafting = false,
  draftError = null,
  onRedraft,
}: {
  archetype: ArchetypeOption | undefined;
  value: CharacterDraft;
  onChange: (next: CharacterDraft) => void;
  /** Claude is drafting the editable fields — show a loader and lock them. */
  drafting?: boolean;
  draftError?: string | null;
  onRedraft?: () => void;
}) {
  const disclosureQ = useQuery({
    queryKey: ['disclosure-policy'],
    queryFn: () => api.disclosurePolicy(),
  });
  const set = <K extends keyof CharacterDraft>(key: K, v: CharacterDraft[K]) =>
    onChange({ ...value, [key]: v });
  const setPillar = (i: number, patch: Partial<Pillar>) =>
    set(
      'pillars',
      value.pillars.map((p, j) => (i === j ? { ...p, ...patch } : p)),
    );
  const pErr = pillarsError(value.pillars);
  const floor = archetype?.claims_floor ?? [];
  const d = disclosureQ.data;

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0">
        <div className="space-y-1">
          <CardTitle>01 · Define the character</CardTitle>
          <CardDescription>
            Claude drafts the editable fields for the selected archetype; change anything and it is
            used exactly as written. Predefined fields come from this deployment's niche and
            disclosure settings and apply to every persona.
          </CardDescription>
        </div>
        {onRedraft && (
          <Button type="button" variant="outline" size="sm" onClick={onRedraft} disabled={drafting}>
            <RefreshCw className="h-3.5 w-3.5" /> Redraft
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-5">
        {drafting && <DraftingOverlay label={archetype?.label} />}
        {draftError && !drafting && (
          <p className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm">
            Couldn't get a draft from Claude ({draftError}). The archetype defaults are filled in
            instead — edit them, or click Redraft.
          </p>
        )}
        <fieldset
          disabled={drafting}
          className={drafting ? 'pointer-events-none space-y-5 opacity-50' : 'space-y-5'}
        >
          <Step
            n={1}
            label="Audience"
            hint="Who the page is for: age, situation, goal, what stops them."
          >
            <Textarea
              rows={2}
              value={value.audience}
              onChange={(e) => set('audience', e.target.value)}
              placeholder="Who the page is for"
            />
          </Step>

          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            <Step n={2} label="Name" hint="Display name on the page (max 30 characters).">
              <Input
                maxLength={30}
                value={value.displayName}
                onChange={(e) => set('displayName', e.target.value)}
                placeholder="Display name"
              />
            </Step>
            <Step
              n={3}
              label="Bio"
              hint={`In-character bio, max ${BIO_SOFT_MAX} characters. The disclosure line is added under it.`}
            >
              <Input
                maxLength={BIO_SOFT_MAX}
                value={value.bio}
                onChange={(e) => set('bio', e.target.value)}
                placeholder="In-character bio"
              />
            </Step>
          </div>

          <Step
            n={4}
            label="Visual identity"
            hint="Look, wardrobe and setting. Anchors the face and every video; Claude derives the palette and style from it."
          >
            <Textarea
              rows={3}
              value={value.visualIdentity}
              onChange={(e) => set('visualIdentity', e.target.value)}
            />
          </Step>

          <Step
            n={5}
            label="Voice"
            hint="3-5 tone words, comma-separated. Claude adds the speech quirks."
          >
            <Input
              value={value.tone}
              onChange={(e) => set('tone', e.target.value)}
              placeholder="e.g. calm, direct, encouraging"
            />
          </Step>

          <Step
            n={6}
            label="Three content pillars"
            hint="Every post is assigned to one of these three."
          >
            <div className="space-y-2">
              {value.pillars.map((p, i) => (
                <div key={i} className="grid grid-cols-1 gap-2 md:grid-cols-[220px_1fr]">
                  <Input
                    value={p.name}
                    onChange={(e) => setPillar(i, { name: e.target.value })}
                    placeholder={`Pillar ${i + 1} name`}
                  />
                  <Input
                    value={p.description ?? ''}
                    onChange={(e) => setPillar(i, { description: e.target.value })}
                    placeholder="What posts in this pillar are about (optional)"
                  />
                </div>
              ))}
              {pErr && <p className="text-xs text-red-600">{pErr}</p>}
            </div>
          </Step>

          <Step
            n={7}
            label="Claims boundaries"
            locked
            hint="Niche rules every persona follows, enforced in the script prompt, the pre-video review and a code check."
          >
            {archetype ? (
              <ul className="space-y-1 rounded-md border border-border bg-muted/30 px-3 py-2 text-sm">
                {floor.map((rule) => (
                  <li key={rule} className="flex gap-2">
                    <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                    {rule}
                  </li>
                ))}
              </ul>
            ) : (
              <Skeleton className="h-24" />
            )}
            <Label className="pt-2 text-xs">
              Extra rules for this persona (editable, one per line)
            </Label>
            <Textarea
              rows={2}
              value={value.extraClaims}
              onChange={(e) => set('extraClaims', e.target.value)}
              placeholder={'e.g. Never compare bodies or rank physiques.'}
            />
          </Step>
        </fieldset>

        <Step
          n={8}
          label="Synthetic-media disclosure"
          locked
          hint="Set per deployment (GHOST_API_DISCLOSE_AI*). Not editable per persona."
        >
          {!d ? (
            <Skeleton className="h-20" />
          ) : d.enabled ? (
            <ul className="space-y-1 rounded-md border border-border bg-muted/30 px-3 py-2 text-sm">
              <li>
                <span className="text-muted-foreground">Bio, last line:</span> {d.bio_line}
              </li>
              <li>
                <span className="text-muted-foreground">Every caption:</span> {d.caption_line}
              </li>
              <li>
                <span className="text-muted-foreground">Every Reel:</span> Instagram “AI info” label
                on (the post is aborted if it can't be set)
              </li>
            </ul>
          ) : (
            <p className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm">
              Disclosure is switched off on this deployment. Turn it on before posting a real page.
            </p>
          )}
        </Step>
      </CardContent>
    </Card>
  );
}
